<script setup lang="ts">
import { AfFrame, AfProgressBar } from '@af-lib/ui-lib';
import { ALButton, ALIconButton, ALLoader } from '@af-lib/vue-ui';
import { toRef } from 'vue';

/* COMPONENTS */
import Text from '@/components/text/Text.vue';
import CopyIconWithTooltip from '@/FNX-Lib/copyIcon/CopyIconWithTooltip.vue';
/* TYPES */
import type { Batch } from '@/types/batches/batches.types';

import { useBatchesConfig } from './useBatchesConfig';

type Props = {
  batch: Batch;
};

export type BatchInfo = Batch & { label: string };

const props = defineProps<Props>();
const emits = defineEmits<{
  (e: 'show-errors', batch: BatchInfo): void;
}>();

const { statusActionButtons, statusInfo, actionTypeConfig } = useBatchesConfig(toRef(props, 'batch'), emits);

function getBatchCompletion() {
  const doneJobs = props.batch.nb_total_operations - props.batch.nb_pending_operations;
  return (doneJobs / props.batch.nb_total_operations) * 100;
}
</script>

<template>
  <AfFrame class="pa-4 mt-3" :hasGradients="false" :enableHover="false">
    <div class="d-flex gap-16 align-center justify-space-between">
      <div>
        <Text type="subtitle2" class="text-uppercase mb-1">
          {{ actionTypeConfig.label }}
        </Text>
        <Text v-if="!props.batch.finished_at" type="body3">
          {{ $t('recruiter_network.queue.panel.card.subtitle', { count: props.batch.nb_total_operations }) }}</Text
        >
      </div>
      <div>
        <Text type="body3" emphasis="regular" class="text-end">
          {{ statusInfo.date }}
        </Text>
        <Text type="caption2" emphasis="regular">
          {{ statusInfo.hour }}
        </Text>
      </div>
    </div>
    <div class="mt-5 d-flex align-center justify-space-between gap-12">
      <!-- COMPLETION -->
      <div class="grow">
        <div class="d-flex align-center gap-8">
          <ALLoader v-if="statusInfo.showLoading" size="xs" />
          <v-icon v-if="statusInfo.icon" :icon="statusInfo.icon" class="text-icon-3" :color="statusInfo.color" />
          <Text :type="statusInfo.textSize || 'body3'"> {{ statusInfo.text }}</Text>

          <ALButton
            v-if="statusInfo.link"
            type="tertiary"
            :label="statusInfo.link.text"
            @click="statusInfo.link.action()"
          />

          <CopyIconWithTooltip v-if="statusInfo.copyData" :data="statusInfo.copyData as string" />
        </div>

        <AfProgressBar
          v-if="statusInfo.showProgress"
          :score="getBatchCompletion()"
          size="x-small"
          color="decor-variant-2"
          style="max-width: 365px"
        />
      </div>
      <!-- CTAs -->
      <div class="d-flex align-center gap-16" style="height: 40px">
        <div v-for="actionBtn in statusActionButtons" :key="actionBtn.label" class="text-end">
          <ALIconButton
            v-if="actionBtn.icon"
            :icon="actionBtn.icon"
            variant="secondary"
            size="s"
            :label="actionBtn.label"
            :isLoading="actionBtn.loading"
            @click="actionBtn.action()"
          />
          <ALButton
            v-else
            type="secondary"
            size="s"
            :isLoading="actionBtn.loading"
            :label="actionBtn.label"
            @click="actionBtn.action()"
          />
        </div>
      </div>
    </div>
  </AfFrame>
</template>
